// Чистая логика доступности поверх ответов /api/map и /api/lengths. Без DOM — под юнит-тесты.
import { addDays, diffNights } from "./dates.js";

// Индекс карты: byDate для О(1) доступа к дню. cats/age — как отдал сервер.
export function indexMap(mapJson) {
  const byDate = new Map();
  for (const day of mapJson.days ?? []) byDate.set(day.date, day);
  return { byDate, cats: mapJson.cats ?? {}, from: mapJson.from, to: mapJson.to, age: mapJson.age };
}

// Вместимость: спальные места (основные + доп.) против взрослых. Дети до maxAgeOfChild
// в Шелтере живут без места и вместимость не занимают. Неизвестная категория не режется.
export function fitsGuests(cat, adults) {
  if (!cat) return true;
  const beds = (cat.mainBeds ?? 0) + (cat.extraBeds ?? 0);
  return beds === 0 || beds >= adults;
}

// Категории дня, подходящие по вместимости; rooms:0 не считается доступностью —
// сервер такие уже отсеивает (фильтр правил в /api/map), здесь защита в глубину
export function dayCats(idx, date, adults) {
  const day = idx.byDate.get(date);
  if (!day) return [];
  return day.cats.filter((c) => (c.rooms ?? 0) > 0 && fitsGuests(idx.cats[c.catId], adults));
}

export const canCheckIn = (idx, date, adults) => dayCats(idx, date, adults).length > 0;

// «от X ₽ за ночь» под датой заезда
export function minNightPrice(idx, date, adults) {
  const cats = dayCats(idx, date, adults);
  return cats.length ? Math.min(...cats.map((c) => c.priceFrom)) : null;
}

export function roomsTotal(idx, date, adults) {
  return dayCats(idx, date, adults).reduce((sum, c) => sum + (c.rooms ?? 0), 0);
}

// Ближайшие доступные заезды вокруг закрытой даты (порт showAlts из legacy): ±span дней, топ-limit
export function nearestAvailable(idx, date, adults, { span = 21, limit = 3 } = {}) {
  const found = [];
  for (let off = 1; off <= span && found.length < limit * 2; off++) {
    for (const cand of [addDays(date, -off), addDays(date, off)]) {
      if (canCheckIn(idx, cand, adults)) found.push(cand);
    }
  }
  found.sort(
    (a, b) => Math.abs(diffNights(date, a)) - Math.abs(diffNights(date, b)) || (a < b ? -1 : 1),
  );
  return found.slice(0, limit);
}

// Кандидаты выезда из /api/lengths: пустой cats = длина запрещена правилами или недоступна.
// Дополнительно фильтр вместимости по словарю cats самого ответа lengths.
export function candidatesFromLengths(lengthsJson, adults) {
  const out = [];
  for (const { L, cats } of lengthsJson.nights ?? []) {
    const fit = cats.filter((c) => fitsGuests(lengthsJson.cats?.[c.catId], adults));
    if (!fit.length) continue;
    out.push({
      L,
      to: addDays(lengthsJson.from, L),
      minTotal: Math.min(...fit.map((c) => c.total)),
    });
  }
  return out;
}

// Кандидаты выезда из /api/checkouts (мгновенный SQLite-ответ, без цен): пустой catIds =
// длина недоступна. minTotal: null → ячейка рисует плейсхолдер до прихода живых цен.
// Вместимость не фильтруем — сервер уже отсёк её по тем же данным (store.categories).
export function candidatesFromCheckouts(json) {
  return (json.nights ?? [])
    .filter((n) => n.catIds.length > 0)
    .map((n) => ({ L: n.L, to: addDays(json.from, n.L), minTotal: null }));
}

// Кандидаты выезда по локальной карте (T37): мгновенно, без сети — выезды кликабельны, пока
// /api/checkouts и цены в пути. В карте — заезды с допустимой длиной по категориям; выезд to
// допустим, если одна категория (по вместимости) есть в карте на всех заездах
// [checkin, to − minStay]: их окна minStay покрывают все ночи. Шахматку по номерам карта
// не видит — её доучтут /api/checkouts и цены.
export function candidatesFromMap(idx, checkin, adults, { minStay = 1, maxStayUi = 21 } = {}) {
  if (!idx || !checkin) return [];
  let maxL = 0;
  for (const { catId } of dayCats(idx, checkin, adults)) {
    let run = 1;
    while (run + minStay - 1 < maxStayUi && hasCat(idx, addDays(checkin, run), catId)) run += 1;
    maxL = Math.max(maxL, Math.min(maxStayUi, run + minStay - 1));
  }
  const out = [];
  for (let L = minStay; L <= maxL; L++)
    out.push({ L, to: addDays(checkin, L), minTotal: null, local: true });
  return out;
}

const hasCat = (idx, date, catId) =>
  (idx.byDate.get(date)?.cats ?? []).some((c) => c.catId === catId && (c.rooms ?? 0) > 0);

// Почему дата не подходит как выезд по карте (T37) — причина для текста под календарём.
// code: min | max | closed (продажи ещё не открыты) | guests | soldout; at — дата заезда окна,
// на которой оборвался ряд (у closed — первая неоткрытая)
export function checkoutVerdict(idx, { checkin, date, adults, minStay = 1, maxStayUi = 21 }) {
  const L = diffNights(checkin, date);
  if (L < minStay) return { ok: false, code: "min", minStay };
  if (L > maxStayUi) return { ok: false, code: "max", maxStayUi };
  if (!idx) return { ok: true };
  let alive = new Set(dayCats(idx, checkin, adults).map((c) => c.catId));
  const lastOpen = [...idx.byDate.keys()].sort().at(-1) ?? null;
  for (let at = checkin; at <= addDays(date, -minStay); at = addDays(at, 1)) {
    const fit = dayCats(idx, at, adults).map((c) => c.catId);
    alive = new Set(fit.filter((id) => alive.has(id)));
    if (alive.size) continue;
    if (!lastOpen || at > lastOpen) return { ok: false, code: "closed", at };
    const any = (idx.byDate.get(at)?.cats ?? []).some((c) => (c.rooms ?? 0) > 0);
    return { ok: false, code: any && !fit.length ? "guests" : "soldout", at };
  }
  return { ok: true };
}

// Источник кандидатов выезда: lengths (живые цены) → checkouts (мгновенно, без цен) → local
// (карта, T37). pending = цены ещё в пути. Длины, которых нет в partial-ответе lengths,
// добираются из запасного источника — «неизвестно» не равно «недоступно».
// null → календарь решает по loading/error (спиннер/ошибка).
export function pickOutCandidates(lengthsSlice, checkoutsSlice, adults, local = []) {
  const fromCheckouts = checkoutsSlice.data ? candidatesFromCheckouts(checkoutsSlice.data) : [];
  const fallback = fromCheckouts.length
    ? { list: fromCheckouts, source: "checkouts" }
    : local.length
      ? { list: local, source: "local" }
      : null;
  if (lengthsSlice.data) {
    const list = candidatesFromLengths(lengthsSlice.data, adults);
    const known = new Set((lengthsSlice.data.nights ?? []).map((n) => n.L));
    const extra = (fallback?.list ?? []).filter((c) => !known.has(c.L));
    return {
      list: [...list, ...extra].sort((a, b) => a.L - b.L),
      pending: false,
      source: "lengths",
    };
  }
  return fallback ? { ...fallback, pending: true } : null;
}

// Автоперелистывание календаря к выездам: индекс месяца для показа — текущий, если в нём
// есть кандидат выезда; иначе месяц самого раннего кандидата; пусто/не нашли → текущий.
// months — список [год, месяц(0-based)] из calendarMonths.
export function monthIndexForCandidates(months, currentIdx, candidateDates) {
  if (!candidateDates?.length) return currentIdx;
  const key = ([y, m]) => `${y}-${String(m + 1).padStart(2, "0")}`;
  const cur = months[currentIdx] ? key(months[currentIdx]) : null;
  if (cur && candidateDates.some((d) => d.startsWith(cur))) return currentIdx;
  const first = [...candidateDates].sort()[0];
  const idx = months.findIndex((m) => first.startsWith(key(m)));
  return idx >= 0 ? idx : currentIdx;
}

// Карточки «Доступные номера»: цены из lengths, витрина (фото/описание/площадь) из map.cats.
// mapCats может отставать от live-ответа — витринные поля тогда просто пустые.
export function cardsFromLengths(lengthsJson, mapCats, L, adults) {
  const night = (lengthsJson.nights ?? []).find((n) => n.L === L);
  if (!night) return [];
  const tariffsById = new Map((lengthsJson.tariffs ?? []).map((t) => [t.id, t]));
  return night.cats
    .filter((c) => fitsGuests(lengthsJson.cats?.[c.catId], adults))
    .map((c) => {
      const info = lengthsJson.cats?.[c.catId] ?? {};
      const shown = mapCats?.[c.catId] ?? {};
      return {
        catId: c.catId,
        name: info.name ?? shown.name ?? `Категория ${c.catId}`,
        beds: (info.mainBeds ?? 0) + (info.extraBeds ?? 0),
        area: info.roomArea ?? shown.roomArea ?? null,
        description: shown.description ?? null,
        photos: shown.photos ?? [],
        tariff: tariffsById.get(c.tariffId) ?? null,
        tariffId: c.tariffId, // явный id — нужен брони (putOrder) и getPaymentOptions (T6)
        rooms: c.rooms,
        total: c.total,
        oldTotal: c.oldTotal ?? null,
        discountPercent: c.discountPercent ?? null,
        prepaymentRub: c.prepaymentRub ?? null,
      };
    })
    .sort((a, b) => a.total - b.total);
}

export function prepaymentPercent(total, prepayment) {
  if (!total || prepayment == null) return null;
  return Math.round((prepayment / total) * 100);
}

// Подпись доли предоплаты (T9.5f): у отеля предоплата — треть стоимости (Шелтер считает 34%),
// гостю понятнее «1/3»; другие доли — процентами. null — подписывать нечего.
export function prepaymentLabel(total, prepayment) {
  const pct = prepaymentPercent(total, prepayment);
  if (!pct) return null;
  const share = prepayment / total;
  return share >= 0.3 && share <= 0.36 ? "1/3" : `${pct}%`;
}

// Индикатор устаревших данных: карта старше 30 минут или возраст неизвестен
export function isStale(age) {
  return age?.seconds == null || age.seconds > 1800;
}
